/**
 * Shared vocabulary for server validation and the web client.
 * Keep labels plain and neutral: a complaint is an allegation or a reported
 * problem until a verification decision or official finding says otherwise.
 */

export const INFRASTRUCTURE_CATEGORIES: { key: string; label: string; issues: string[] }[] = [
  { key: "roads", label: "Roads", issues: ["Potholes or damaged road", "Unfinished road work", "Missing footpath", "Broken streetlight", "Encroachment", "Other road issue"] },
  { key: "water_supply", label: "Water supply", issues: ["No water supply", "Irregular supply", "Contaminated water", "Leaking pipeline", "Low pressure", "Other water issue"] },
  { key: "electricity", label: "Electricity", issues: ["Frequent outages", "Dangerous wiring or pole", "Transformer fault", "Incorrect billing", "New connection delayed", "Other electricity issue"] },
  { key: "drainage", label: "Drainage & sewerage", issues: ["Blocked drain", "Overflowing sewer", "Waterlogging", "Open manhole", "Other drainage issue"] },
  { key: "sanitation", label: "Garbage & sanitation", issues: ["Garbage not collected", "Illegal dumping", "Public toilet not maintained", "Burning of waste", "Other sanitation issue"] },
  { key: "schools", label: "Schools & education", issues: ["Teacher absence", "Unsafe building", "Mid-day meal problem", "Lack of facilities", "Admission denied", "Other school issue"] },
  { key: "health", label: "Hospitals & public health", issues: ["Doctor or staff absence", "Medicines unavailable", "Denial of treatment", "Unhygienic facility", "Ambulance delay", "Other health issue"] },
  { key: "transport", label: "Public transport", issues: ["Irregular service", "Unsafe vehicle", "Overcharging", "Bus stop / station condition", "Other transport issue"] },
  { key: "schemes", label: "Government schemes", issues: ["Benefit not received", "Wrongful exclusion", "Application not processed", "Asked for documents repeatedly", "Other scheme issue"] },
  { key: "records_services", label: "Certificates, records & services", issues: ["Certificate delayed", "Property mutation / registration", "Ration card", "Name or detail error in records", "Other service issue"] },
  { key: "other_public_service", label: "Other public service", issues: ["Other"] },
];

export const ALLEGATION_TYPES: { key: string; label: string }[] = [
  { key: "corruption", label: "Corruption" },
  { key: "bribery", label: "Bribery" },
  { key: "misconduct", label: "Misconduct / misbehaviour" },
  { key: "harassment", label: "Harassment" },
  { key: "abuse_of_authority", label: "Abuse of authority" },
  { key: "negligence", label: "Negligence" },
  { key: "delay", label: "Unreasonable delay" },
  { key: "refusal_of_duty", label: "Refusal of duty" },
  { key: "dereliction_of_duty", label: "Dereliction of duty" },
];

export const COMPLAINT_STATUS_LABELS: Record<string, string> = {
  submitted: "Submitted",
  acknowledged: "Acknowledged",
  under_review: "Under review",
  assigned: "Assigned",
  in_progress: "In progress",
  escalated: "Escalated",
  resolved: "Resolved",
  closed: "Closed",
  reopened: "Reopened",
  withdrawn: "Withdrawn by complainant",
};

export const VERIFICATION_LABELS: Record<string, string> = {
  unverified: "Not yet verified",
  evidence_reviewed: "Evidence reviewed",
  verified: "Verified by Seva Sangrah",
  disputed: "Disputed",
  unsubstantiated: "Not substantiated",
};

export const FINDING_TYPE_LABELS: Record<string, string> = {
  verification_decision: "Verification decision",
  evidence_review: "Evidence review",
  investigation_finding: "Investigation finding",
  official_response: "Official response",
  representative_response: "Representative response",
  final_outcome: "Final outcome",
};

export const FINAL_OUTCOMES: { key: string; label: string; closes: "resolved" | "closed" }[] = [
  { key: "resolved", label: "Problem resolved", closes: "resolved" },
  { key: "partially_resolved", label: "Partially resolved", closes: "resolved" },
  { key: "action_taken", label: "Administrative action taken", closes: "resolved" },
  { key: "referred", label: "Referred to competent authority", closes: "closed" },
  { key: "no_action", label: "No action by authority", closes: "closed" },
  { key: "insufficient_evidence", label: "Insufficient evidence", closes: "closed" },
  { key: "withdrawn", label: "Withdrawn by complainant", closes: "closed" },
  { key: "duplicate", label: "Duplicate of another record", closes: "closed" },
  { key: "out_of_scope", label: "Outside Seva Sangrah's scope (private dispute)", closes: "closed" },
  { key: "dismissed", label: "Dismissed by competent authority", closes: "closed" },
];

export const VERIFICATION_OUTCOMES = ["evidence_reviewed", "verified", "disputed", "unsubstantiated"] as const;

export const PROMISE_CATEGORIES = [
  "Roads & transport", "Water", "Electricity", "Health", "Education", "Sanitation", "Housing", "Employment",
  "Agriculture", "Safety & policing", "Environment", "Welfare schemes", "Governance & transparency", "Other",
];

export const PROMISE_TYPES: { key: string; label: string }[] = [
  { key: "manifesto", label: "Manifesto commitment" },
  { key: "public_speech", label: "Public speech or rally" },
  { key: "official_announcement", label: "Official announcement" },
  { key: "budget_commitment", label: "Budget commitment" },
  { key: "commitment", label: "Other public commitment" },
  { key: "other", label: "Other" },
];

export const PROMISE_MAKER_TYPES = ["MP", "MLA", "Former Incumbent", "Candidate", "Government Department", "Public Institution", "Other"] as const;

export const PROMISE_SCOPES: { key: string; label: string }[] = [
  { key: "community", label: "Community" },
  { key: "ward", label: "Ward" },
  { key: "city", label: "City" },
  { key: "constituency", label: "Constituency" },
  { key: "district", label: "District" },
  { key: "state", label: "State" },
  { key: "national", label: "National" },
];

export const PROMISE_STATUS_LABELS: Record<string, string> = {
  announced: "Announced",
  in_progress: "In progress",
  fulfilled: "Fulfilled",
  partially_fulfilled: "Partially fulfilled",
  unfulfilled: "Unfulfilled",
  broken: "Broken",
};

export const INDIAN_STATES = [
  "Andaman and Nicobar Islands", "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chandigarh", "Chhattisgarh",
  "Dadra and Nagar Haveli and Daman and Diu", "Delhi", "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jammu and Kashmir",
  "Jharkhand", "Karnataka", "Kerala", "Ladakh", "Lakshadweep", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya",
  "Mizoram", "Nagaland", "Odisha", "Puducherry", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
  "Uttar Pradesh", "Uttarakhand", "West Bengal",
];

export const FOUNDING_CONTRIBUTIONS = [
  { id: "awareness", title: "Awareness", description: "Help citizens understand Seva Sangrah and participate." },
  { id: "verification", title: "Verification", description: "Help verify information, complaints and public records." },
  { id: "leadership", title: "Leadership Identification", description: "Help identify citizens and public servants who demonstrate leadership." },
  { id: "memory", title: "Institutional Memory", description: "Help preserve what the Republic should remember and learn from." },
];
export const FOUNDING_AVAILABILITY = [
  { id: "full_time", label: "Full-time" },
  { id: "part_time", label: "Part-time" },
  { id: "weekends", label: "Weekends" },
];
export const CIVIL_SOCIETY_TYPES = ["NGO", "Civil Society Organisation", "Community Organisation", "Research Institution", "Other"];
export const CIVIL_SOCIETY_CONTRIBUTIONS = [
  "Community networks", "Civic awareness", "Research", "Field implementation", "Data / technology",
  "Legal expertise", "Public policy expertise", "Training", "Communications", "Other",
];
export const EXPERT_CONTRIBUTIONS = [
  "Public policy", "Law and constitutional affairs", "Research and academia", "Technology and data", "Communications",
  "Governance", "Design and user experience", "Training and capacity building", "Media and storytelling", "Other",
];
export const EXPERT_AVAILABILITY = [
  { id: "occasional", label: "Occasionally" },
  { id: "monthly", label: "Monthly" },
  { id: "regular", label: "Regularly" },
  { id: "project_based", label: "On specific projects" },
];
export const CONNECT_INTERESTS = [
  "Explore a partnership",
  "Discuss leadership / institutional collaboration",
  "Explore research or policy collaboration",
  "Explore technology or implementation",
  "Something else",
];

/**
 * Active-emergency detection (EDGE-19). A match shows emergency numbers
 * alongside the form; it never blocks filing the civic record.
 */
export const EMERGENCY_KEYWORDS = [
  "fire", "burning", "on fire", "accident", "bleeding", "unconscious", "heart attack", "not breathing", "drowning",
  "collapsed", "electrocuted", "live wire", "gas leak", "explosion", "attack", "assault", "kidnap", "being beaten",
  "suicide", "flood", "trapped", "आग", "दुर्घटना", "बेहोश", "डूब",
];
export const EMERGENCY_NUMBERS = [
  { number: "112", label: "National emergency" },
  { number: "100", label: "Police" },
  { number: "101", label: "Fire" },
  { number: "108", label: "Ambulance" },
  { number: "1098", label: "Childline" },
  { number: "181", label: "Women helpline" },
];
export function looksLikeEmergency(text: string): boolean {
  const t = text.toLowerCase();
  return EMERGENCY_KEYWORDS.some((k) => new RegExp(`(^|[^\\p{L}])${k.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}([^\\p{L}]|$)`, "u").test(t));
}

export const REPORT_REASONS = [
  { key: "spam", label: "Spam" },
  { key: "fake_or_misleading", label: "Fake or misleading" },
  { key: "duplicate", label: "Duplicate" },
  { key: "abusive", label: "Abusive" },
  { key: "other", label: "Other" },
] as const;
